import type { ReactNode, PointerEvent as ReactPointerEvent } from "react"
import { useCallback, useEffect, useRef, useState } from "react"
import { IconColorPaletteOutline18 as PaletteIcon } from "nucleo-ui-outline-18"

import { cn, USER_COLOR_PALETTE } from "@productlane/lib/src/browser"
import { Input, Popover } from "@productlane/ui"

import type { Hsv } from "@/lib/color-hsv"
import { textContrastColor } from "@/components/color-picker"
import { hexToHsv, hsvToHex, normalizeHex } from "@/lib/color-hsv"

const clamp01 = (value: number) => Math.min(1, Math.max(0, value))

const PRESETS = Object.values(USER_COLOR_PALETTE).map(({ hex }) =>
	hex.toUpperCase(),
)

interface ColorSwatchPickerProps {
	value: string
	onChange: (hex: string) => void
	onCommit?: (hex: string) => void
	disabled?: boolean
	children?: ReactNode
	className?: string
}

export function ColorSwatchPicker({
	value,
	onChange,
	onCommit,
	disabled,
	children,
	className,
}: ColorSwatchPickerProps) {
	const [open, setOpen] = useState(false)
	const [hsv, setHsv] = useState<Hsv>(
		() => hexToHsv(value) ?? { h: 0, s: 0, v: 0 },
	)
	const [hexDraft, setHexDraft] = useState(() => normalizeHex(value) ?? value)

	// Hue and saturation are unrecoverable from black/white hex, so only re-derive
	// when the incoming value is a different color than the one we produced.
	useEffect(() => {
		const incoming = normalizeHex(value)
		if (!incoming || incoming === hsvToHex(hsv)) return
		const next = hexToHsv(incoming)
		if (next) setHsv(next)
	}, [value, hsv])

	useEffect(() => {
		setHexDraft(normalizeHex(value) ?? value)
	}, [value])

	const emit = useCallback(
		(next: Hsv, commit?: boolean) => {
			setHsv(next)
			const hex = hsvToHex(next)
			onChange(hex)
			if (commit) onCommit?.(hex)
		},
		[onChange, onCommit],
	)

	const trigger = children ?? (
		<button
			type="button"
			disabled={disabled}
			aria-label="Pick color"
			className={cn(
				"group relative h-8 w-8 flex-none rounded-md shadow-sm ring-2 ring-black/15 hover:ring-black/50 disabled:opacity-50 data-[state=open]:ring-black/50 dark:ring-white/15 dark:hover:ring-white/50 dark:data-[state=open]:ring-white/50",
				className,
			)}
			style={{ background: value }}
		>
			<span
				className="flex h-full w-full items-center justify-center rounded-md opacity-0 group-hover:opacity-100 group-data-[state=open]:opacity-100"
				style={{
					color:
						textContrastColor(normalizeHex(value) ?? "#000000") ?? "#FFFFFF",
				}}
			>
				<PaletteIcon className="h-4 w-4 flex-none drop-shadow-sm" />
			</span>
		</button>
	)

	if (disabled) return trigger

	return (
		<Popover
			align="start"
			className="w-60"
			open={open}
			onOpenChange={setOpen}
			onOpenAutoFocus={(e) => {
				e.preventDefault()
			}}
			content={
				<div className="flex flex-col gap-3">
					<SaturationArea hsv={hsv} onChange={emit} />
					<HueSlider hsv={hsv} onChange={emit} />
					<div className="flex items-center gap-2">
						<div
							className="h-7 w-7 flex-none rounded-md ring-1 ring-black/10 dark:ring-white/15"
							style={{ background: hsvToHex(hsv) }}
						/>
						<Input
							name="hex"
							value={hexDraft}
							maxLength={7}
							spellCheck={false}
							placeholder="#000000"
							className="w-full"
							onChange={(e) => {
								setHexDraft(e.target.value)
								const parsed = hexToHsv(e.target.value)
								if (parsed) {
									setHsv(parsed)
									onChange(hsvToHex(parsed))
								}
							}}
							onBlur={() => {
								const normalized = normalizeHex(hexDraft)
								if (normalized) {
									onCommit?.(normalized)
									setHexDraft(normalized)
								} else {
									setHexDraft(hsvToHex(hsv))
								}
							}}
						/>
					</div>
					<div className="flex items-center justify-between gap-1">
						{PRESETS.map((preset) => (
							<button
								key={preset}
								type="button"
								aria-label={preset}
								className={cn(
									"h-5 w-5 flex-none rounded-full shadow-[inset_0_0_0_1px_rgba(0,0,0,0.12)] ring-2 ring-offset-2 ring-offset-white dark:ring-offset-gray-800",
									normalizeHex(value) === preset
										? "ring-gray-900 dark:ring-white"
										: "ring-transparent hover:ring-black/30 dark:hover:ring-white/40",
								)}
								style={{ background: preset }}
								onClick={() => {
									const parsed = hexToHsv(preset)
									if (!parsed) return
									setHsv(parsed)
									onChange(preset)
									onCommit?.(preset)
									setOpen(false)
								}}
							/>
						))}
					</div>
				</div>
			}
		>
			{trigger}
		</Popover>
	)
}

function useDragTrack(
	onPosition: (position: { x: number; y: number }, commit: boolean) => void,
) {
	const ref = useRef<HTMLDivElement | null>(null)

	const read = useCallback(
		(clientX: number, clientY: number, commit: boolean) => {
			const rect = ref.current?.getBoundingClientRect()
			if (!rect || rect.width === 0 || rect.height === 0) return
			onPosition(
				{
					x: clamp01((clientX - rect.left) / rect.width),
					y: clamp01((clientY - rect.top) / rect.height),
				},
				commit,
			)
		},
		[onPosition],
	)

	const onPointerDown = useCallback(
		(e: ReactPointerEvent<HTMLDivElement>) => {
			e.preventDefault()
			e.currentTarget.setPointerCapture(e.pointerId)
			ref.current?.focus()
			read(e.clientX, e.clientY, false)
		},
		[read],
	)

	const onPointerMove = useCallback(
		(e: ReactPointerEvent<HTMLDivElement>) => {
			if (!e.currentTarget.hasPointerCapture(e.pointerId)) return
			read(e.clientX, e.clientY, false)
		},
		[read],
	)

	const onPointerUp = useCallback(
		(e: ReactPointerEvent<HTMLDivElement>) => {
			if (!e.currentTarget.hasPointerCapture(e.pointerId)) return
			e.currentTarget.releasePointerCapture(e.pointerId)
			read(e.clientX, e.clientY, true)
		},
		[read],
	)

	return { ref, onPointerDown, onPointerMove, onPointerUp }
}

interface AreaProps {
	hsv: Hsv
	onChange: (hsv: Hsv, commit?: boolean) => void
}

function SaturationArea({ hsv, onChange }: AreaProps) {
	const drag = useDragTrack(
		useCallback(
			({ x, y }, commit) => {
				onChange({ h: hsv.h, s: x, v: 1 - y }, commit)
			},
			[hsv.h, onChange],
		),
	)

	return (
		// eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
		<div
			{...drag}
			role="application"
			aria-label="Saturation and brightness"
			// eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
			tabIndex={0}
			data-testid="saturation-area"
			className="relative h-36 w-full touch-none rounded-lg ring-1 ring-inset ring-black/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 dark:ring-white/10 dark:focus-visible:ring-white"
			style={{
				background: `linear-gradient(to top, #000, rgba(0,0,0,0)), linear-gradient(to right, #fff, ${hsvToHex(
					{ h: hsv.h, s: 1, v: 1 },
				)})`,
			}}
			onKeyDown={(e) => {
				const step = e.shiftKey ? 0.1 : 0.02
				if (e.key === "ArrowLeft")
					onChange({ ...hsv, s: clamp01(hsv.s - step) }, true)
				else if (e.key === "ArrowRight")
					onChange({ ...hsv, s: clamp01(hsv.s + step) }, true)
				else if (e.key === "ArrowUp")
					onChange({ ...hsv, v: clamp01(hsv.v + step) }, true)
				else if (e.key === "ArrowDown")
					onChange({ ...hsv, v: clamp01(hsv.v - step) }, true)
				else return
				e.preventDefault()
			}}
		>
			<div
				className="pointer-events-none absolute h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-[0_1px_3px_rgba(0,0,0,0.4)]"
				style={{
					left: `${hsv.s * 100}%`,
					top: `${(1 - hsv.v) * 100}%`,
					background: hsvToHex(hsv),
				}}
			/>
		</div>
	)
}

function HueSlider({ hsv, onChange }: AreaProps) {
	const drag = useDragTrack(
		useCallback(
			({ x }, commit) => {
				onChange({ ...hsv, h: x * 360 }, commit)
			},
			[hsv, onChange],
		),
	)

	return (
		<div
			{...drag}
			role="slider"
			aria-label="Hue"
			aria-valuemin={0}
			aria-valuemax={360}
			aria-valuenow={Math.round(hsv.h)}
			tabIndex={0}
			data-testid="hue-slider"
			className="relative h-3 w-full touch-none rounded-full ring-1 ring-inset ring-black/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 dark:ring-white/10 dark:focus-visible:ring-white"
			style={{
				background:
					"linear-gradient(to right, #FF0000, #FFFF00, #00FF00, #00FFFF, #0000FF, #FF00FF, #FF0000)",
			}}
			onKeyDown={(e) => {
				const step = e.shiftKey ? 20 : 4
				if (e.key === "ArrowLeft")
					onChange({ ...hsv, h: Math.max(0, hsv.h - step) }, true)
				else if (e.key === "ArrowRight")
					onChange({ ...hsv, h: Math.min(360, hsv.h + step) }, true)
				else return
				e.preventDefault()
			}}
		>
			<div
				className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-[0_1px_3px_rgba(0,0,0,0.4)]"
				style={{
					left: `${(hsv.h / 360) * 100}%`,
					background: hsvToHex({ h: hsv.h, s: 1, v: 1 }),
				}}
			/>
		</div>
	)
}
